<template>
    <b-container fluid class="mt-3">
        <b-card title="Monitoring" class="mb-3">
            <b-link v-if="isAdmin" @click="clearCache()" class="card-link">
                <b-icon icon="arrow-repeat"></b-icon>
                Clear cache
            </b-link>
        </b-card>

        <b-row class="mb-3">
            <b-col md="6">
                <b-list-group>
                    <monitoring-line id-key="app" :error="isAppError" :is-alive="isAlive"
                                     label="Connection to the application"/>
                    <monitoring-line id-key="db" :error="isDatabaseError" :is-alive="isDatabaseAlive"
                                     label="Connection to the database"/>
                    <monitoring-line id-key="av" :error="isAntivirusError" :is-alive="isAntivirusAlive"
                                     label="Connection to the antivirus" :message="true"/>
                </b-list-group>
            </b-col>
            <b-col md="6">
                <b-list-group>
                    <b-list-group-item v-if="isAdmin">
                        <b-form-checkbox v-model="isDealUnderMaintenance" @change="setDealMaintenance" name="check-button" switch>
                            Deal maintenance mode
                        </b-form-checkbox>
                    </b-list-group-item>
                    <b-list-group-item v-if="isAdmin">
                        <b-form-checkbox v-model="isClassicUnderMaintenance" @change="setClassicMaintenance" name="check-button" switch>
                            Classic maintenance mode
                        </b-form-checkbox>
                    </b-list-group-item>
                </b-list-group>
            </b-col>
        </b-row>

        <b-row class="mb-3">
            <b-col md="6">
                <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" align="fill"
                              aria-controls="my-clients"></b-pagination>

                <b-list-group id="clients-list" v-if="clients != null">
                    <b-list-group-item class="text-center" v-if="clients.length === 0">
                        No client instance found
                    </b-list-group-item>
                    <b-list-group-item button v-for="(client, index) in clientsForList"
                                       :key="index"
                                       :active="index === currentIndex"
                                       @click="setActiveClient(client, index)">
                        <span>{{ client.name }}</span>
                    </b-list-group-item>
                </b-list-group>
            </b-col>
            <b-col md="6">
                <div v-if="currentClient">
                    <b-card no-body>
                        <template #header>
                            <div class="d-flex align-items-center">
                                <h4 class="card-title mb-0 mr-auto">{{ currentClient.name }}</h4>
                                <b-link @click="refreshMonitoring(currentClient.id)" class="link-custom">
                                    <b-icon icon="arrow-repeat"></b-icon>
                                    Force refresh
                                </b-link>
                            </div>
                        </template>

                        <b-overlay :show="!showConnectionStatus" spinner-variant="primary" spinner-type="grow" spinner-small
                            style="min-height: 100px">
                            <b-list-group flush v-if="showConnectionStatus && connectionStatus">
                                <monitoring-line v-for="(co, key, index) in connectionStatus" :key="index"
                                                 :id-key="'line-'+index" :error="co.message" :is-alive="co.alive"
                                                 :label="'Connection to ' + key"/>
                            </b-list-group>
                        </b-overlay>
                    </b-card>
                </div>
            </b-col>
        </b-row>

    </b-container>

</template>

<script>
import OppService from "@/services/OppService";
import ClientDataService from "@/services/ClientDataService";
import CacheService from "@/services/CacheService";
import MonitoringLine from "@/components/configuration/MonitoringLine";
const AppsEnum = Object.freeze({
    OPP: 'OPP',
    DEAL: 'DEAL',
    CLASSIC: 'CLASSIC',
});

export default {
    name: "Monitoring",
    created() {
        document.title = "Configuration : Monitoring";
    },
    components: {
        MonitoringLine,
    },
    data() {
        return {
            isAlive: null,
            isAppError: null,
            isAntivirusAlive: null,
            isAntivirusError: null,
            isDatabaseAlive: null,
            isDatabaseError: null,
            isDealUnderMaintenance: null,
            isClassicUnderMaintenance: null,
            clients: null,
            currentClient: null,
            currentIndex: -1,
            connectionStatus: null,
            showConnectionStatus: false,
            perPage: 10,
            currentPage: 1,
        }
    },
    computed: {
        isAdmin() {
            return this.$store.getters.isAdmin;
        },
        rows() {
            return this.clients ? this.clients.length : 0;
        },
        clientsForList() {
            if (this.clients) {
                return this.clients.slice(
                    (this.currentPage - 1) * this.perPage,
                    this.currentPage * this.perPage,
                );
            } else {
                return null;
            }
        }
    },
    mounted() {
        OppService.isAlive()
            .then(response => {
                this.isAlive = response.data;
            })
            .catch(error => {
                this.isAppError = error;
                this.isAlive = false;
            });
        OppService.isAntivirusAlive()
            .then(response => {
                this.isAntivirusAlive = response.data;
            })
            .catch(error => {
                this.isAntivirusError = error;
                this.isAntivirusAlive = false;
            });
        OppService.isDatabaseAlive()
            .then(response => {
                this.isDatabaseAlive = response.data;
            })
            .catch(error => {
                this.isDatabaseError = error;
                this.isDatabaseAlive = false;
            });
        this.getClients();
        OppService.isUnderMaintenance(AppsEnum.DEAL)
            .then(response => {
                this.isDealUnderMaintenance = response.data;
            })
            .catch(error => {
                console.log(error);
                this.isDealUnderMaintenance = false;
            });
        OppService.isUnderMaintenance(AppsEnum.CLASSIC)
            .then(response => {
                this.isClassicUnderMaintenance = response.data;
            })
            .catch(error => {
                console.log(error);
                this.isClassicUnderMaintenance = false;
            });
    },
    methods: {
        getClients() {
            ClientDataService.getAll()
                .then(response => (this.clients = response.data))
                .catch(error => console.log(error))
        },
        setActiveClient(client, index) {
            this.currentClient = client;
            this.currentIndex = index;
            this.showConnectionStatus = false;
            this.testConnection();
        },
        testConnection() {
            ClientDataService.testConnection(this.currentClient.id)
                .then(response => {
                    this.connectionStatus = response.data;
                    this.showConnectionStatus = true;
                })
                .catch(error => console.log(error));
        },
        refreshMonitoring(id) {
            this.connectionStatus = null;
            this.showConnectionStatus = false;
            CacheService.clearClientMonitoring(id)
                .then(response => {
                    console.log(response);
                    ClientDataService.testConnection(id)
                        .then(response => {
                            this.connectionStatus = response.data;
                            this.showConnectionStatus = true;
                        })
                        .catch(error => console.log(error));
                })
                .catch(error => {
                    console.log(error);
                });
        },
        clearCache() {
            this.$bvModal.msgBoxConfirm('Are you sure you want to clear the cache ?', {
                title: 'Please Confirm',
                okTitle: 'YES',
                cancelTitle: 'NO',
                hideHeaderClose: false,
            })
                .then(value => {
                    if (value) {
                        this.$bvModal.show('loading-modal');
                        CacheService.clearAll()
                            .then(response => {
                                if (response) {
                                    this.$parent.displaySuccessMessage('The cache was cleared.');
                                }
                            })
                            .catch(error => {
                                console.log(error);
                                this.$parent.displayErrorMessage('An error occurred while trying to clear the cache.');
                            });
                    }
                })
                .catch(error => console.log(error));
        },
        setDealMaintenance() {
            this.$bvModal.msgBoxConfirm('Are you sure you want to change Deal maintenance mode ?', {
                title: 'Please Confirm',
                okTitle: 'YES',
                cancelTitle: 'NO',
                hideHeaderClose: false,
            })
                .then(value => {
                    if (value) {
                        this.$bvModal.show('loading-modal');
                        OppService.setMaintenance(AppsEnum.DEAL, this.isDealUnderMaintenance)
                            .then(response => {
                                if (response.data.message) {
                                    this.$parent.displaySuccessMessage(response.data.message);
                                }
                            })
                            .catch(error => {
                                console.log(error);
                                this.isDealUnderMaintenance = !this.isDealUnderMaintenance;
                                this.$parent.displayErrorMessage('An error occurred while trying to set Deal maintenance mode.');
                            });
                    } else {
                        this.isDealUnderMaintenance = !this.isDealUnderMaintenance;
                    }
                })
                .catch(error => console.log(error));
        },
        setClassicMaintenance() {
            this.$bvModal.msgBoxConfirm('Are you sure you want to change Classic maintenance mode ?', {
                title: 'Please Confirm',
                okTitle: 'YES',
                cancelTitle: 'NO',
                hideHeaderClose: false,
            })
                .then(value => {
                    if (value) {
                        this.$bvModal.show('loading-modal');
                        OppService.setMaintenance(AppsEnum.CLASSIC, this.isClassicUnderMaintenance)
                            .then(response => {
                                if (response.data.message) {
                                    this.$parent.displaySuccessMessage(response.data.message);
                                }
                            })
                            .catch(error => {
                                console.log(error);
                                this.isClassicUnderMaintenance = !this.isClassicUnderMaintenance;
                                this.$parent.displayErrorMessage('An error occurred while trying to set Classic maintenance mode.');
                            });
                    } else {
                        this.isClassicUnderMaintenance = !this.isClassicUnderMaintenance;
                    }
                })
                .catch(error => console.log(error));
        },
    }
}
</script>

<style scoped>
    #clients-list {
        max-height: 500px;
        overflow-y: auto;
    }

    .link-custom {
        text-decoration: none !important;
        text-transform: uppercase;
    }
</style>